import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

import AddTeamMembersModal from '../components/AddTeamMembersModal';
import TransferOwnershipModal from '../components/TransferOwnershipModal';
import UnifiedTimeline from '../components/UnifiedTimeline';
import ScrollableDropdown from '../components/ScrollableDropdown';
import { useAuth } from '../context/AuthContext';


const ApplicationTimeline = () => {
  const { applicationId: urlAppId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedAppId, setSelectedAppId] = useState(urlAppId || '');
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);

  const [, setIsOwner] = useState(false);

  // Instant UI: Load initial state from sessionStorage if available
  const [application, setApplication] = useState(() => {
    if (!selectedAppId) return null;
    const cached = sessionStorage.getItem(`appInfo_${selectedAppId}`);
    return cached ? JSON.parse(cached) : null;
  });
  const [bugs, setBugs] = useState(() => {
    if (!selectedAppId) return [];
    const cached = sessionStorage.getItem(`appBugs_${selectedAppId}`);
    return cached ? JSON.parse(cached) : [];
  });
  const [, setTesterBugStats] = useState(() => {
    if (!selectedAppId) return { open: 0, inProgress: 0, fixed: 0 };
    const cached = sessionStorage.getItem(`appStats_${selectedAppId}`);
    return cached ? JSON.parse(cached) : { open: 0, inProgress: 0, fixed: 0 };
  });
  const [loading, setLoading] = useState(!!selectedAppId && !application);

  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Fetch applications list for dropdown
  useEffect(() => {
    const fetchApps = async () => {
      setLoadingApps(true);
      try {
        // Use for-bug-reporting endpoint as it contains teamMembers and isTeamMember flag
        const { data } = await axios.get('/api/applications/for-bug-reporting');
        let accessibleApps = data.data || [];

        if (user?.role === 'tester') {
          // Requirement: Testers only see apps they are team members of
          accessibleApps = accessibleApps.filter(app => app.isTeamMember);
        } else if (user?.role === 'developer') {
          // Requirement: Developers see apps they created/owned or are team members of
          const userId = user._id || user.id;
          accessibleApps = accessibleApps.filter(app => {
            const isOwner = app.ownerId?._id === userId || app.ownerId === userId ||
              app.developerId?._id === userId || app.developerId === userId;
            const isTeamMember = app.teamMembers?.some(m => (m.userId?._id || m.userId) === userId);
            return isOwner || isTeamMember;
          });
        }

        setApplications(accessibleApps);
      } catch (error) {
        console.error('Failed to fetch applications:', error);
        toast.error('Failed to load applications list');
      } finally {
        setLoadingApps(false);
      }
    };

    if (user) {
      fetchApps();
    }
  }, [user]);

  const fetchApplicationData = useCallback(async (appId) => {
    if (!appId) return;

    setLoading(true);
    try {
      const [appRes, bugsRes, statsRes] = await Promise.all([
        axios.get(`/api/applications/${appId}`),
        axios.get(`/api/bugs?application=${appId}&pageSize=100`),
        axios.get(`/api/bugs/stats?application=${appId}`).catch(() => ({ data: { data: {} } }))
      ]);

      const appData = appRes.data.data;
      const appWithDefaults = {
        ...appData,
        createdBy: appData.developerId || appData.createdBy,
        testerName: 'N/A'
      };

      const bugsData = (bugsRes.data.data || []).map(bug => ({
        ...bug,
        statusHistory: bug.statusHistory || [{
          status: bug.status || 'Open',
          changedBy: bug.reportedBy || { name: 'Unknown', role: 'tester' },
          changedAt: bug.createdAt,
          description: ''
        }]
      }));

      if (bugsData.length > 0) {
        appWithDefaults.testerName = bugsData[0].reportedBy?.name || 'N/A';
      }

      const stats = {
        open: statsRes.data.data?.openBugs || 0,
        inProgress: statsRes.data.data?.inProgressBugs || 0,
        fixed: statsRes.data.data?.fixedBugs || 0
      };

      setApplication(appWithDefaults);
      setBugs(bugsData);
      setTesterBugStats(stats);

      sessionStorage.setItem(`appInfo_${appId}`, JSON.stringify(appWithDefaults));
      sessionStorage.setItem(`appBugs_${appId}`, JSON.stringify(bugsData));
      sessionStorage.setItem(`appStats_${appId}`, JSON.stringify(stats));

    } catch (error) {
      console.error('Failed to fetch application data:', error);
      toast.error('Failed to load application timeline');
      setApplication(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedAppId) {
      fetchApplicationData(selectedAppId);
    }
  }, [selectedAppId, fetchApplicationData]);

  useEffect(() => {
    const currentUserId = user?._id || user?.id;
    if (application && user && currentUserId) {
      const ownerId = application.ownerId?._id || application.ownerId || application.developerId?._id || application.developerId;
      const isOwnerInTeam = application.teamMembers?.some(member => {
        const memberId = member.userId?._id || member.userId;
        const memberOwnerId = application.ownerId?._id || application.ownerId;
        return memberId === currentUserId && memberId === memberOwnerId;
      });
      const ownerMatch = ownerId === currentUserId || isOwnerInTeam;
      setIsOwner(ownerMatch);
    }
  }, [application, user]);

  const handleAppChange = (appId) => {
    setSelectedAppId(appId);
    if (appId) {
      navigate(`/applications/${appId}/timeline`, { replace: true });
    } else {
      navigate('/view-timeline', { replace: true });
    }
  };

  const dropdownOptions = applications.map(app => ({
    value: app._id,
    label: app.name,
    description: app.version ? `Version: ${app.version}` : undefined,
    meta: <span className={`text-[10px] px-2 py-0.5 rounded-full ${app.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{app.status}</span>
  }));

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header with Title and Dropdown */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Activity Timeline</h1>
            {application && (
              <p className="text-sm text-gray-500 mt-1">
                Viewing activity for <span className="font-semibold text-indigo-600">{application.name}</span>
              </p>
            )}
          </div>

          <div className="w-full md:w-72">
            <ScrollableDropdown
              value={selectedAppId}
              onChange={handleAppChange}
              options={dropdownOptions}
              placeholder="Select Application..."
              disabled={loadingApps}
              buttonHeightClassName="h-10"
              buttonClassName="bg-white shadow-sm border-gray-200 text-sm font-medium"
              searchable={true}
              searchPlaceholder="Filter apps..."
            />
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-sm text-gray-500 font-medium">Fetching history...</p>
          </div>
        ) : !selectedAppId ? (
          <div className="flex flex-col items-center justify-center py-24 bg-white rounded-xl border-2 border-dashed border-gray-200 text-center">
            <div className="bg-indigo-50 p-5 rounded-full mb-4">
              <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 tracking-tight">No Application Selected</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
              Use the dropdown menu at the top-right to pick an application and view its full lifecycle timeline and activity history.
            </p>
          </div>
        ) : !application ? (
          <div className="text-center py-24 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 bg-red-50 rounded-full w-fit mx-auto mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Application Invalid</h1>
            <p className="text-gray-500 mt-2 text-sm">We couldn't retrieve the details for this application.</p>
            <button
              onClick={() => setSelectedAppId('')}
              className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-sm font-bold text-sm"
            >
              Back to Overview
            </button>
          </div>
        ) : (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <UnifiedTimeline bugs={bugs} mode="tree" />
          </div>
        )}
      </div>

      {/* Modals remain same, but check if application exists before passing props */}
      {application && (
        <>
          <AddTeamMembersModal
            isOpen={showAddMembersModal}
            onClose={() => setShowAddMembersModal(false)}
            applicationId={selectedAppId}
            onSuccess={() => fetchApplicationData(selectedAppId)}
          />

          <TransferOwnershipModal
            isOpen={showTransferModal}
            onClose={() => setShowTransferModal(false)}
            applicationId={selectedAppId}
            teamMembers={application?.teamMembers || []}
            currentOwnerId={application?.ownerId?._id || application?.ownerId}
            onSuccess={() => fetchApplicationData(selectedAppId)}
          />
        </>
      )}
    </div>
  );
};

export default ApplicationTimeline;
